/**
 * @fileoverview The subscribe page for a member tending someone else's garden (B23; owner, 2026-10-20: "the invitee
 * should only use the inviters trial not their own"): the garden owner's plan, which is the one the garden uses, and
 * the server's line saying so ("You're tending Friday's garden on their plan."). No purchase buttons: a member never
 * acts on the owner's subscription (the server refuses it too, 403 TENDING_OWNER_PLAN), and manages their own plan
 * from their own garden.
 *
 * The server decides all of it: GET /billing/status answers the owner's access and plans with tendingFor (whose,
 * and the line, in the reader's language). The page only words the owner's state with the header's own words
 * (billing.line.*: "9 days of Grow More left", "Free", "Paused until March 1", "Founding gardener · free until
 * February 28", "Gift · until December 25, 2027"; "Grow More" while subscribed), and shows the plan comparison so
 * the member can see what the plan includes.
 *
 * Where: SubscribePage, in place of the status, the plans and their buttons, when the status has tendingFor.
 * API: none of its own (the billing status comes in as a prop).
 * Words (en/es): billing.line.*, billing.compare.grow; the line itself is the server's (todo_word billing.tending.line).
 *
 * @module TendingPlan
 */

import React from 'react';
import { DateTime } from 'luxon';
import { useTranslation } from '@/shared/hooks/useTranslation';
import type { BillingStatus, StatusAccess } from '@/shared/api/billing';
import { PlanComparison } from './PlanComparison';
import { pauseDay } from '../lib/pauseDay';
import { foundingDay } from '../lib/foundingDay';

/**
 * A YYYY-MM-DD day from the server as "February 28" (with its year when asked), in the app's language; '' when it
 * does not read.
 *
 * @param day - A day, not a moment.
 * @param language - The app's language.
 * @param withYear - Say the year too.
 */
function dayWords(day: string | null | undefined, language: string, withYear = false): string {
  const at = day ? DateTime.fromISO(day) : null;
  if (!at?.isValid) return '';
  return at.setLocale(language).toLocaleString(withYear ? { month: 'long', day: 'numeric', year: 'numeric' } : { month: 'long', day: 'numeric' });
}

/**
 * The owner's plan in the header's words: what the plan is now, with nothing offered.
 *
 * @param access - The owner's access, as the server sends it.
 * @param t - The translator.
 * @param language - The app's language.
 * @returns The words.
 */
function ownerPlanWords(
  access: StatusAccess, t: (key: string, params?: Record<string, string | number>) => string, language: string,
): string {
  const line = access.statusLine;
  // The server's kind says which state the owner's plan is in (C118: no state of the app's own).
  switch (line.kind) {
    case 'paused': {
      const until = pauseDay(line.until, language);
      return until ? t('billing.line.paused', { date: until }) : t('billing.line.pausedOpen');
    }
    case 'pausedOpen': return t('billing.line.pausedOpen');
    case 'active': case 'ending': case 'paymentProblem': case 'pauseAhead': return t('billing.compare.grow');
    case 'free': return t('billing.line.free');
    case 'founding': {
      const until = foundingDay(line.date, language);
      return until ? `${t('billing.line.founding')} ${t('billing.line.foundingUntil', { date: until })}` : t('billing.line.founding');
    }
    case 'gift': {
      const until = dayWords(line.date, language, true);
      return until ? `${t('billing.line.gift')} ${t('billing.line.giftUntil', { date: until })}` : t('billing.line.gift');
    }
    case 'trialWaiting': return t('billing.line.trialWaiting', { days: line.days ?? 0 });
    case 'trialOne': return t('billing.line.trialOne');
    default: return t('billing.line.trial', { days: line.days ?? 0 });
  }
}

/**
 * The owner's plan and the line saying the member is on it, with what the plan includes; no buttons.
 *
 * @param props.data - The billing status, with tendingFor.
 */
export const TendingPlan: React.FC<{ data: BillingStatus & { tendingFor: NonNullable<BillingStatus['tendingFor']> } }> = ({ data }) => {
  const { reactTranslate: t, i18n: { language } } = useTranslation();
  return (
    <section className="mt-2">
      <p className="max-w-prose text-foreground">{data.tendingFor.line}</p>
      <p className="mt-2 text-sm text-muted-foreground">{ownerPlanWords(data.access, t, language)}</p>
      <PlanComparison className="mt-6" rows={data.plans.rows} free={data.plans.free} />
    </section>
  );
};
